<!-- Kanaal-weergave is de fallback zodra er geen geldige poort-context (meer) is —
     een context-refresh die de actieve poort verliest, herstelt dus vanzelf. -->
@if (activeGateContext() === null && activeChannelDocRef() === null) {
  <div class="hd-channel-info" [class.hd-channel-info--composite-base]="isCompositeBaseStyle()">
    @for (section of sections(); track section.channelKey) {
      <section class="hd-channel-info__section">
        <header class="hd-channel-info__header">
          <div class="hd-channel-info__titles">
            <div class="hd-channel-info__title-row">
              <h4 class="hd-channel-info__name">{{ section.nameLabel }}</h4>
              <button
                type="button"
                class="hd-channel-info__open-doc"
                [title]="fullInfoAriaLabel(section.channelKey)"
                [attr.aria-label]="fullInfoAriaLabel(section.channelKey)"
                (click)="openChannelDocument(section.channelKey)"
              >
                <i class="fas fa-circle-info" aria-hidden="true"></i>
              </button>
            </div>
            <p class="hd-channel-info__description">{{ section.descriptionLabel }}</p>
          </div>
          <!-- Twee klikbare poort-blokjes: elk opent de detailinfo van die poort. -->
          <div class="hd-channel-info__gates-badge">
            @for (gate of section.gates; track gate) {
              <button
                type="button"
                class="hd-channel-info__gate-badge-item"
                [disabled]="!canOpenGate(gate)"
                [attr.aria-label]="gateBadgeAriaLabel(gate)"
                (click)="openGate(gate)"
              >{{ gate }}</button>
            }
          </div>
        </header>

        <div class="hd-channel-info__circuit-row">
          @if (section.circuitLabel) {
            <span class="hd-channel-info__circuit-chip">
              @if (section.circuitThemeIcon) {
                <i [class]="'fa-regular fa-' + section.circuitThemeIcon" aria-hidden="true"></i>
              }
              {{ section.circuitLabel }}
            </span>
          }
          @if (section.centersLabel) {
            <span class="hd-channel-info__centers">{{ section.centersLabel }}</span>
          }
        </div>

        @if (section.emotionalWave; as wave) {
          <!-- Emotionele golf van het kanaal (enkel de zeven Solar Plexus-kanalen). -->
          <hd-emotional-wave-row
            class="hd-channel-info__wave"
            data-test-id="channel-info-emotional-wave"
            [wave]="wave.wave"
            [keynoteKey]="wave.keynoteKey"
            [sourceLines]="wave.sourceLines"
            [definedChannelKeys]="definedEmotionalChannelKeys()"
          />
        }

        <p class="hd-channel-info__activations-title">{{ planetActivationsTitle() }}</p>
        @for (row of section.rows; track row.key) {
          <div
            class="hd-channel-info__row"
            [class.hd-channel-info__row--design]="row.isDesign"
            [class.hd-channel-info__row--personality]="!row.isDesign && !row.isTransit && !row.transitKind"
            [class.hd-channel-info__row--transit]="row.isTransit"
            [class.hd-channel-info__row--just-now-transit]="row.transitKind === 'just-now' || row.transitKind === 'cycle'"
          >
            <span class="hd-channel-info__planet-glyph" aria-hidden="true">{{ row.glyph }}</span>
            <div class="hd-channel-info__row-main">
              <span class="hd-channel-info__side-planet">
                {{ row.sideLabel }} ·
                <span
                  class="hd-channel-info__planet-name"
                  [title]="row.planetSubtitle || null"
                >{{ row.planetName }}</span>
              </span>
              <span class="hd-channel-info__line">{{ row.lineLabel }}</span>
            </div>
            <button
              type="button"
              class="hd-channel-info__gate-chip"
              [attr.aria-label]="row.gateChipLabel"
              (click)="openGate(row.gate)"
            >
              <span class="hd-channel-info__iching" aria-hidden="true">{{ row.ichingChar }}</span>
              <span class="hd-channel-info__gate-chip-label">{{ row.gateChipLabel }}</span>
            </button>
          </div>
        } @empty {
          <p class="hd-channel-info__no-activations">{{ noActivationsLabel() }}</p>
        }
      </section>
    }
  </div>
} @else {
  @if (activeChannelDocRef(); as channelDocRef) {
  <div class="hd-channel-info hd-channel-info--doc-view">
    <div class="hd-channel-info__back-bar">
      <button
        type="button"
        class="hd-channel-info__back-button"
        [attr.aria-label]="backAriaLabel()"
        (click)="backToChannel()"
      >
        <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
        {{ backLabel() }}
      </button>
    </div>

    <hd-doc-viewer [initialRef]="channelDocRef" [embedded]="true" />

    <div class="hd-channel-info__back-bar hd-channel-info__back-bar--bottom">
      <button
        type="button"
        class="hd-channel-info__back-button"
        [attr.aria-label]="backAriaLabel()"
        (click)="backToChannel()"
      >
        <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
        {{ backLabel() }}
      </button>
    </div>
  </div>
  } @else {
  <div class="hd-channel-info hd-channel-info--gate-view">
    <div class="hd-channel-info__back-bar">
      <button
        type="button"
        class="hd-channel-info__back-button"
        [attr.aria-label]="backAriaLabel()"
        (click)="backToChannel()"
      >
        <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
        {{ backLabel() }}
      </button>
    </div>

    @if (activeGateContext(); as gateContext) {
      @if (isLarge()) {
        <hd-gate-info-full
          [context]="gateContext"
          [sectionVariant]="'c'"
          (collapseRequested)="shrink()"
        />
      } @else {
        <hd-gate-info-small [context]="gateContext" (expandRequested)="enlarge()" />
      }
    }

    <div class="hd-channel-info__back-bar hd-channel-info__back-bar--bottom">
      <button
        type="button"
        class="hd-channel-info__back-button"
        [attr.aria-label]="backAriaLabel()"
        (click)="backToChannel()"
      >
        <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
        {{ backLabel() }}
      </button>
    </div>
  </div>
  }
}
